import React, { FC, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Delete as DeleteIcon } from '@material-ui/icons';
import { useToaster } from '@/components/core/Toaster';
import {
  CREATE_DATA_EXPORT_CHANNEL,
  CreateDataExportChannelData,
  CreateDataExportChannelVars,
  DataExportDeliveryChannel,
  VOID_DATA_EXPORT_CHANNEL,
  VoidDataExportChannelData,
  VoidDataExportChannelVars,
} from '@/graph/data-export-admin';
import { formatChannelConfig, useSharedStyles, useTimestampFormatter } from './shared';

/**
 * Channels configuration dialog.
 *
 * Channels are an export-configuration concern — they shape WHERE
 * the runs from the Manual / Scheduled tabs deliver to, but they
 * aren't a workflow that the operator drives day-to-day. So they
 * live behind a settings cog next to the page title rather than as
 * a top-level tab.
 *
 * Cardinality is small (handful of S3 buckets per org), so the list
 * is shown as a single non-paginated table.
 *
 * The free-form JSON config field from the previous design is gone —
 * the form renders type-specific fields (s3: bucket, local:
 * directory) so the operator never has to know what shape the JSON
 * needs. The dialog builds the JSON itself before submission. Adding
 * a new channel type means: extend `CHANNEL_TYPE_FORMS` here, extend
 * the matching validator in `view-api/.../DataExportAdmin.scala`,
 * `view-functions/.../common-export`, and the seed script.
 */
export const ChannelsDialog: FC<{
  open: boolean;
  onClose: () => void;
  organizationId: number;
  channels: DataExportDeliveryChannel[];
  channelsLoading: boolean;
  refetchChannels: () => void;
}> = ({ open, onClose, organizationId, channels, channelsLoading, refetchChannels }) => {
  const classes = useSharedStyles();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Delivery channels</DialogTitle>
      <DialogContent dividers>
        <div className={classes.dialogSection}>
          <CreateChannelForm organizationId={organizationId} onCreated={refetchChannels} />
          <Divider />
          <ChannelsList
            organizationId={organizationId}
            channels={channels}
            loading={channelsLoading}
            onChanged={refetchChannels}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ---------- per-type form definitions ---------- */

type ChannelFieldSpec = {
  key: string;
  label: string;
  helperText: string;
  placeholder: string;
};

type ChannelTypeForm = {
  value: string;
  label: string;
  description: string;
  fields: ChannelFieldSpec[];
  /** Build the JSONB config from the operator's form values. */
  buildConfig: (values: Record<string, string>) => Record<string, string>;
};

const CHANNEL_TYPE_FORMS: ChannelTypeForm[] = [
  {
    value: 's3',
    label: 'S3 bucket',
    description: 'Streams the merged file to an S3 bucket via CopyObject from the staging bucket.',
    fields: [
      {
        key: 'bucket',
        label: 'Bucket name',
        // Admin-only surface — operational pointers are appropriate here.
        helperText:
          'Buckets named ospitek-data-export-dest-{env}-* are writable out of the box (merger IAM wildcard); ' +
          'provision them via ospitek-scripts/scripts/data-export/setup-export-bucket.sh --purpose destination. ' +
          'Any other bucket needs a one-time grant at onboarding: ospitek-scripts/scripts/data-export/grant-destination-bucket.sh. ' +
          'An ungranted bucket fails the delivery with AccessDenied — redeliverable after the grant.',
        placeholder: 'ospitek-data-export-dest-prod-customer',
      },
    ],
    buildConfig: values => ({ bucket: values.bucket?.trim() ?? '' }),
  },
  {
    value: 'local',
    label: 'Local filesystem (dev only)',
    description: 'Writes the produced file to a local directory. Intended for local development; not for production.',
    fields: [
      {
        key: 'directory',
        label: 'Directory path',
        helperText: 'Absolute path on the worker host. Operator must ensure the path exists and is writable.',
        placeholder: '/tmp/data-export',
      },
    ],
    buildConfig: values => ({ directory: values.directory?.trim() ?? '' }),
  },
];

// UI-only gate. The DB CHECK constraint, the Scala validator, the TS
// Zod schemas, and the worker's delivery registry all still accept
// `local` in every environment — production deployments rely on
// convention (and SQL seed scripts) to keep `local` channels out.
// This filter prevents the operator-facing channel dropdown from
// offering `local` in non-dev builds, so a misclick can't create a
// `type='local'` row that points at the prod Lambda's ephemeral
// filesystem. Dev builds (`import.meta.env.DEV`) still see it for
// LocalStack / local-FS round-trips.
//
// The `import.meta` cast bypasses tsconfig coverage of Vite's
// ambient types — `vite-env.d.ts` lives at the repo root, outside
// the `src` include path on this project, so TS doesn't see the
// extended `ImportMeta` interface here.
const isDevBuild = (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;
const VISIBLE_CHANNEL_TYPE_FORMS: ChannelTypeForm[] = isDevBuild
  ? CHANNEL_TYPE_FORMS
  : CHANNEL_TYPE_FORMS.filter(t => t.value !== 'local');

/* ---------- create form ---------- */

const CreateChannelForm: FC<{ organizationId: number; onCreated: () => void }> = ({ organizationId, onCreated }) => {
  const classes = useSharedStyles();
  const toaster = useToaster();
  const [name, setName] = useState('');
  // Default to the first VISIBLE type so prod builds (where `local`
  // is filtered out) don't start with a stale `type='local'` that
  // would be unrepresentable in the dropdown.
  const [type, setType] = useState<string>(VISIBLE_CHANNEL_TYPE_FORMS[0]!.value);
  // Keyed map of field-key → string value. Cleared when the type
  // changes so a stale s3 bucket value doesn't bleed into a local
  // directory submit (and vice versa).
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const [createChannel, { loading }] = useMutation<CreateDataExportChannelData, CreateDataExportChannelVars>(
    CREATE_DATA_EXPORT_CHANNEL
  );

  // Lookup uses the full list (not the visible one) so existing
  // helper text and schemas still resolve if a future code path
  // surfaces a hidden type — UI gating doesn't remove the
  // form definition, just the dropdown option.
  const activeForm = useMemo(
    () => CHANNEL_TYPE_FORMS.find(t => t.value === type) ?? VISIBLE_CHANNEL_TYPE_FORMS[0]!,
    [type]
  );

  // Every per-type field must be non-empty before submit is allowed —
  // the server-side validator would reject empty values anyway, but
  // surfacing the constraint up front keeps the round-trip noise
  // out of the toaster.
  const allFieldsFilled = useMemo(
    () => activeForm.fields.every(f => (fieldValues[f.key] ?? '').trim().length > 0),
    [activeForm, fieldValues]
  );
  const disabled = !name.trim() || !allFieldsFilled;

  const onTypeChange = (next: string) => {
    setType(next);
    setFieldValues({});
  };

  const onSubmit = async () => {
    const config = activeForm.buildConfig(fieldValues);
    try {
      const { data } = await createChannel({
        variables: {
          organizationId,
          name: name.trim(),
          type,
          configJson: JSON.stringify(config),
        },
      });
      const ch = data?.createDataExportChannel;
      if (!ch) {
        toaster.error('Channel creation returned no data');
        return;
      }
      toaster.success(`Channel "${ch.name}" created`);
      setName('');
      setFieldValues({});
      onCreated();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toaster.error(`Channel creation failed: ${message}`);
    }
  };

  return (
    <div className={classes.dialogFormStack}>
      <div>
        <Typography variant="subtitle2">Add a channel</Typography>
        <Typography variant="body2" color="textSecondary">
          Channels are org-scoped and reusable. The name must be unique among active channels.
        </Typography>
      </div>
      <div className={classes.dialogFormStackTight}>
        <div className={classes.controlsRow}>
          <TextField
            label="Name"
            size="small"
            variant="outlined"
            className={classes.fieldName}
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" variant="outlined" className={classes.fieldType}>
            <InputLabel shrink>Type</InputLabel>
            <Select value={type} onChange={e => onTypeChange(e.target.value as string)} label="Type" disabled={loading}>
              {VISIBLE_CHANNEL_TYPE_FORMS.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {activeForm.fields.map(field => (
            <TextField
              key={field.key}
              label={field.label}
              size="small"
              variant="outlined"
              className={classes.fieldConfig}
              value={fieldValues[field.key] ?? ''}
              onChange={e => setFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
              placeholder={field.placeholder}
              helperText={field.helperText}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
          ))}
        </div>
        <Typography variant="caption" color="textSecondary">
          {activeForm.description}
        </Typography>
      </div>
      <div className={classes.actionRow}>
        <Button variant="contained" color="primary" size="small" onClick={onSubmit} disabled={loading || disabled}>
          {loading ? <CircularProgress size={16} className={classes.buttonProgress} /> : null}
          Create channel
        </Button>
      </div>
    </div>
  );
};

/* ---------- list ---------- */

const ChannelsList: FC<{
  organizationId: number;
  channels: DataExportDeliveryChannel[];
  loading: boolean;
  onChanged: () => void;
}> = ({ organizationId, channels, loading, onChanged }) => {
  const classes = useSharedStyles();
  return (
    <div className={classes.dialogFormStack}>
      <div>
        <Typography variant="subtitle2">Active channels</Typography>
        <Typography variant="body2" color="textSecondary">
          Voiding a channel blocks future dispatches against it. Existing delivery history stays intact — the snapshot
          saved at delivery-row insert time is what made the actual upload decision, so historical rows remain
          interpretable even after a void.
        </Typography>
      </div>
      {channels.length === 0 && !loading ? (
        <div className={classes.tableEmpty}>
          <Typography variant="body2">No active channels. Add one above to start delivering exports.</Typography>
        </div>
      ) : (
        <TableContainer className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.map(ch => (
                <ChannelRow key={ch.id} channel={ch} organizationId={organizationId} onVoided={onChanged} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {loading && channels.length > 0 && (
        <Typography variant="caption" color="textSecondary">
          Refreshing…
        </Typography>
      )}
    </div>
  );
};

const ChannelRow: FC<{
  channel: DataExportDeliveryChannel;
  organizationId: number;
  onVoided: () => void;
}> = ({ channel, organizationId, onVoided }) => {
  const classes = useSharedStyles();
  const toaster = useToaster();
  const fmt = useTimestampFormatter();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [voidChannel, { loading }] = useMutation<VoidDataExportChannelData, VoidDataExportChannelVars>(
    VOID_DATA_EXPORT_CHANNEL
  );

  const onConfirmVoid = async () => {
    try {
      const { data } = await voidChannel({ variables: { organizationId, channelId: channel.id } });
      if (data?.voidDataExportChannel) {
        toaster.success(`Channel "${channel.name}" voided`);
      } else {
        toaster.error('Channel was already voided');
      }
      setConfirmOpen(false);
      onVoided();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toaster.error(`Void failed: ${message}`);
    }
  };

  return (
    <TableRow hover>
      <TableCell className={classes.cellNowrap}>{channel.name}</TableCell>
      <TableCell className={classes.cellNowrap}>{channel.type}</TableCell>
      <TableCell className={classes.configMono}>{formatChannelConfig(channel.type, channel.configJson)}</TableCell>
      <TableCell className={classes.cellTimestamp}>{fmt(channel.createdAt)}</TableCell>
      <TableCell align="center">
        <Tooltip title="Void channel">
          <span>
            <IconButton size="small" onClick={() => setConfirmOpen(true)} disabled={loading} aria-label="void-channel">
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>{`Void channel "${channel.name}"?`}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Voiding a channel blocks all future dispatches against it. Existing delivery history is unaffected. Any
              schedule still attached to this channel will silently skip it at the next tick — detach explicitly if you
              want the schedule itself to surface as zero-channel.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={onConfirmVoid} color="secondary" disabled={loading}>
              {loading ? <CircularProgress size={16} className={classes.buttonProgress} /> : null}
              Void channel
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    </TableRow>
  );
};
